<template>
  <div class="wrap-evolucao">
    <div class="header-resumo">
      <div v-if="municipioLogo" class="header-resumo__logo">
        <img :src="municipioLogo" alt="Logo do município" />
      </div>

      <div class="header-resumo__content">
        <h1 class="header-resumo__title">
          {{ municipioNomeUf }}
        </h1>

        <div class="header-resumo__badges">
          <span class="header-resumo__badge">
            {{ resultadoPeriodo }}
          </span>

          <span class="header-resumo__meta">
            <i class="icofont-users-social"></i>
            {{ totalEquipes }}
            {{ totalEquipes === 1 ? 'Equipe avaliada' : 'Equipes avaliadas' }}
          </span>
        </div>

        <div class="header-resumo__progress">
          <div class="header-resumo__progress-top">
            <span>Com 100% de recursos federais na APS</span>

            <span
              class="header-resumo__progress-val"
              :style="{ color: barFillColor }"
            >
              {{ percentualOtimas }}% das equipes
            </span>
          </div>

          <div class="header-resumo__bar">
            <div
              class="header-resumo__bar-fill"
              :style="{
                width: `${percentualOtimas}%`,
                background: barFillColor,
              }"
            ></div>
          </div>
        </div>
      </div>

      <div class="header-resumo__actions">
        <button
          type="button"
          class="header-resumo__btn header-resumo__btn--light"
          @click="imprimirDados"
        >
          <i class="icofont-print"></i>
          Imprimir
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Evolucao',
  props: {
    periodo: {
      type: Object,
      default: null,
    },
    totalEquipes: {
      type: Number,
      default: 0,
    },
    totalAvaliacoes: {
      type: Number,
      default: 0,
    },
    totalAvaliacoesOtimas: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    /**
     * Retorna o nome do município.
     *
     * @return {String}
     */
    municipioNomeUf() {
      return this.$store.state.cidade || ''
    },

    /**
     * Retorna o logotipo configurado para o município.
     *
     * @return {String}
     */
    municipioLogo() {
      const brasao = this.$store.state?.infoMunicipio?.brasao

      if (brasao?.trim()) {
        return `https://esusfeedback.com.br${brasao}`
      }

      const logotipo = this.$store.state?.infoMunicipio?.logotipo

      if (logotipo?.trim()) {
        return logotipo
      }

      return require('@/assets/images/logo.png')
    },

    /**
     * Retorna o período selecionado no componente Componentes.
     *
     * @return {String}
     */
    resultadoPeriodo() {
      const periodo = this.periodo?.label || this.periodo?.value

      return periodo ? `RESULTADO ${periodo}` : 'RESULTADO'
    },

    /**
     * Calcula o percentual de avaliações classificadas como ótimas.
     *
     * Cada resultado de cada indicador é contabilizado individualmente.
     *
     * @return {Number}
     */
    percentualOtimas() {
      const total = Number(this.totalAvaliacoes || 0)
      const otimas = Number(this.totalAvaliacoesOtimas || 0)

      if (!total) {
        return 0
      }

      return Math.round((otimas / total) * 100)
    },

    /**
     * Retorna a cor da barra conforme o percentual de equipes ótimas.
     *
     * @return {String}
     */
    barFillColor() {
      const percentual = Number(this.percentualOtimas || 0)

      if (percentual <= 25) {
        return '#c46500'
      }

      if (percentual <= 50) {
        return '#d5ba0a'
      }

      if (percentual <= 75) {
        return '#38ba38'
      }

      return '#1212b6'
    },
  },

  methods: {
    /**
     * Abre a impressão do conteúdo.
     *
     * @return {void}
     */
    imprimirDados() {
      this.$nextTick(() => {
        window.print()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.wrap-evolucao {
  width: 100%;
  margin: 30px auto 0;
}

.header-resumo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  padding: 24px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.04);
  border-radius: 28px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.06);
}

.header-resumo__logo {
  flex: 0 0 auto;

  img {
    display: block;
    width: 120px;
    height: 120px;
    padding: 10px;
    object-fit: contain;
    background: #fff;
    border: 2px solid rgba(0, 0, 0, 0.03);
    border-radius: 24px;
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
  }
}

.header-resumo__content {
  flex: 1 1 320px;
  min-width: 260px;
}

.header-resumo__title {
  margin: 0 0 8px;
  color: #1f2937;
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.4px;
  text-transform: uppercase;
}

.header-resumo__badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.header-resumo__badge {
  padding: 6px 10px;
  color: #4f46e5;
  background: rgba(99, 102, 241, 0.12);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.header-resumo__meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;

  i {
    font-size: 13px;
  }
}

.header-resumo__progress {
  width: 100%;
  max-width: 520px;
}

.header-resumo__progress-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  color: #6b7280;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.header-resumo__bar {
  width: 100%;
  height: 12px;
  overflow: hidden;
  background: #f3f4f6;
  border-radius: 999px;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.06);
}

.header-resumo__bar-fill {
  height: 100%;
  border-radius: inherit;
  transition: width 0.6s ease, background 0.6s ease;
}

.header-resumo__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: auto;
}

.header-resumo__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  color: #475569;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.25s ease;

  &:hover {
    background: #f8fafc;
  }
}

@media screen and (max-width: 807px) {
  .header-resumo {
    flex-direction: column;
  }

  .header-resumo__content {
    width: 100%;
    min-width: 0;
  }

  .header-resumo__actions {
    margin-left: 0;
  }
}

@media screen and (max-width: 656px) {
  .header-resumo__progress-top {
    flex-direction: column;
  }
}
</style>
